"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type DayRetreatsDisplayPreviewProps = {
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

/*
 * Golden hour at a lakeside retreat: a low sun in the notch between layered
 * hazy ridges, its reflection running down the water to a wooden jetty where
 * one figure sits in meditation (the focal point). A tree with paper
 * lanterns and pampas grass frames the left; a glowing bell tent with string
 * lights frames the right. The near end of the jetty runs under the
 * participant pill.
 *
 * Motion: the sun's reflection shimmers and the lanterns breathe — CSS
 * transform and opacity only, off under reduced motion.
 */
const RETREAT_KEYFRAMES = `
@keyframes retreat-shimmer {
  0%, 100% { transform: translate3d(-2px, 0, 0); opacity: 0.75 }
  50% { transform: translate3d(2px, 0, 0); opacity: 1 }
}
@keyframes retreat-glow { 0%, 100% { opacity: 0.75 } 50% { opacity: 1 } }
.retreat-shimmer { animation: retreat-shimmer 3.6s ease-in-out infinite }
.retreat-glow { animation: retreat-glow 3s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) {
  .retreat-shimmer, .retreat-glow { animation: none }
}
`;

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `retreat-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

const HORIZON = 112;

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id("water")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#ffd6a4" />
          <stop offset="0.25" stopColor="#e9a08a" />
          <stop offset="0.6" stopColor="#7d6684" />
          <stop offset="1" stopColor="#3b3a5e" />
        </linearGradient>
        <linearGradient id={id("plank")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#c48a58" />
          <stop offset="1" stopColor="#7a4a26" />
        </linearGradient>
        <radialGradient id={id("lantern")}>
          <stop offset="0" stopColor="#fff1c4" />
          <stop offset="0.5" stopColor="#ffb85c" />
          <stop offset="1" stopColor="#ff9a3c" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={id("tent")} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#fff4e2" />
          <stop offset="0.6" stopColor="#ecd6b8" />
          <stop offset="1" stopColor="#b39373" />
        </linearGradient>
        <linearGradient id={id("bank")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#5d6a3a" />
          <stop offset="1" stopColor="#2a3020" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** Ridges + lake surface — stretch with the card. */
function Landscape({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 300 220"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M0 84 L30 72 L62 82 L98 64 L128 88 L150 98 L172 86 L204 66 L238 80 L268 70 L300 82 L300 112 L0 112 Z"
        fill="#d79a92"
        opacity="0.7"
      />
      <path
        d="M0 98 L40 86 L84 96 L118 92 L142 106 L160 106 L188 92 L226 90 L262 98 L300 90 L300 112 L0 112 Z"
        fill="#9a6676"
        opacity="0.85"
      />
      <path
        d="M0 106 Q50 100 100 108 Q150 112 200 106 Q250 100 300 107 L300 113 L0 113 Z"
        fill="#5d4560"
      />
      <rect
        x="0"
        y={HORIZON}
        width="300"
        height={220 - HORIZON}
        fill={`url(#${id("water")})`}
      />
      {/* Ridge reflections */}
      <path
        d="M0 113 Q50 118 100 114 Q150 112 200 115 Q250 119 300 114 L300 116 L0 116 Z"
        fill="#5d4560"
        opacity="0.45"
      />
      {/* Ripple lines */}
      {[124, 138, 156, 178, 202].map((y, i) => (
        <path
          key={`ripple-${y}`}
          d={`M${10 + i * 7} ${y} H${120 - i * 4} M${180 + i * 4} ${y + 2} H${290 - i * 6}`}
          stroke="#fff1dc"
          strokeOpacity={0.22 - i * 0.025}
          strokeWidth="0.8"
        />
      ))}
    </svg>
  );
}

// Kept left of the label pill (x < 62 above y 38).
const LEAVES: Array<[number, number, number, number]> = [
  [6, 12, 26, 14],
  [30, 26, 24, 12],
  [10, 42, 20, 10],
  [46, 50, 22, 10],
  [72, 62, 18, 8],
  [24, 2, 22, 9],
];

const PAMPAS: Array<[number, number, number]> = [
  [8, 176, -14],
  [20, 168, -4],
  [32, 172, 8],
  [44, 182, 16],
];

function Shore({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute top-0 left-1/2 -translate-x-1/2"
      width="300"
      height="220"
      viewBox="0 0 300 220"
      aria-hidden="true"
      focusable="false"
    >
      {/* Sun reflection column */}
      <g className="retreat-shimmer">
        {[
          [116, 22],
          [121, 18],
          [127, 16],
          [134, 12],
          [142, 10],
          [151, 7],
        ].map(([y, w]) => (
          <rect
            key={`glint-${y}`}
            x={150 - w}
            y={y}
            width={w * 2}
            height="1.6"
            rx="0.8"
            fill="#fff6dc"
            opacity="0.85"
          />
        ))}
      </g>

      {/* Birds */}
      <path
        d="M104 52 q4 -3 7 0 q3 -3 7 0 M120 62 q3 -2 5 0 q2 -2 5 0"
        stroke="#5a3a40"
        strokeWidth="1.1"
        strokeLinecap="round"
        fill="none"
      />

      {/* Lily pads + lotus */}
      {[
        { x: 112, y: 150, s: 1 },
        { x: 194, y: 156, s: 0.85 },
      ].map((pad) => (
        <g
          key={`pad-${pad.x}`}
          transform={`translate(${pad.x} ${pad.y}) scale(${pad.s})`}
        >
          <ellipse cx="0" cy="0" rx="10" ry="3" fill="#4f7a4a" />
          <path d="M0 0 L8 -1.6" stroke="#2f4f2c" strokeWidth="0.8" />
          <path d="M-3 -1 Q-4 -6 0 -7 Q4 -6 3 -1 Z" fill="#ffb6c8" />
          <path d="M-1.4 -1 Q0 -8 1.4 -1 Z" fill="#ffe1ea" />
        </g>
      ))}

      {/* Jetty */}
      <polygon
        points="138,130 162,130 206,220 94,220"
        fill={`url(#${id("plank")})`}
      />
      {[134, 139, 145, 152, 160, 170, 182, 196, 212].map((y) => {
        const t = (y - 130) / 90;
        return (
          <line
            key={`plank-${y}`}
            x1={138 - t * 44}
            x2={162 + t * 44}
            y1={y}
            y2={y}
            stroke="#5b3416"
            strokeOpacity="0.6"
            strokeWidth={0.6 + t}
          />
        );
      })}
      <path
        d="M138 130 L94 220"
        stroke="#ffd2a0"
        strokeOpacity="0.6"
        strokeWidth="1"
      />
      <rect x="136" y="126" width="2.4" height="9" fill="#5b3416" />
      <rect x="161.6" y="126" width="2.4" height="9" fill="#5b3416" />

      {/* Meditating figure, rim-lit by the sun */}
      <ellipse cx="150" cy="140" rx="9" ry="2" fill="#5b3416" opacity="0.4" />
      <g fill="#3d2a38">
        <ellipse cx="150" cy="129" rx="10" ry="3.4" />
        <path d="M145 128 L146.5 116 Q150 113 153.5 116 L155 128 Z" />
        <circle cx="150" cy="110.5" r="3.6" />
        <circle cx="150" cy="106.4" r="1.6" />
      </g>
      <path
        d="M146.4 117 Q141.5 122 142 128 M153.6 117 Q158.5 122 158 128"
        stroke="#3d2a38"
        strokeWidth="2.2"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M147 107.5 Q150 105.6 153 107.5 M147 116 Q150 113.5 153 116"
        stroke="#ffd9a0"
        strokeWidth="0.9"
        fill="none"
      />
    </svg>
  );
}

/** Pinned to the card edges so the banks meet the frame at any width. */
function LeftBank({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute top-0 left-0"
      width="100"
      height="220"
      viewBox="0 0 100 220"
      aria-hidden="true"
      focusable="false"
    >
      {/* Left bank: tree, lanterns, pampas */}
      <path
        d="M0 140 Q30 136 62 148 Q80 160 84 220 L0 220 Z"
        fill={`url(#${id("bank")})`}
      />
      <path
        d="M14 220 Q10 160 22 120 Q30 90 18 52 M22 120 Q46 92 82 70"
        stroke="#3a2a22"
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M17 52 Q18 32 6 14"
        stroke="#3a2a22"
        strokeWidth="3.4"
        strokeLinecap="round"
        fill="none"
      />
      {LEAVES.map(([cx, cy, rx, ry]) => (
        <g key={`leaf-${cx}-${cy}`}>
          <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="#4a5532" />
          <ellipse
            cx={cx + 3}
            cy={cy + 3}
            rx={rx * 0.85}
            ry={ry * 0.7}
            fill="#36402a"
          />
          <path
            d={`M${cx - rx * 0.7} ${cy - ry * 0.4} Q${cx} ${cy - ry * 1.1} ${cx + rx * 0.7} ${cy - ry * 0.4}`}
            stroke="#e8c88a"
            strokeOpacity="0.55"
            strokeWidth="1"
            fill="none"
          />
        </g>
      ))}
      {[
        { x: 50, y: 80, from: 56 },
        { x: 80, y: 82, from: 66 },
      ].map((lantern) => (
        <g key={`lantern-${lantern.x}`}>
          <line
            x1={lantern.x}
            x2={lantern.x}
            y1={lantern.from}
            y2={lantern.y - 6}
            stroke="#3a2a22"
            strokeWidth="0.7"
          />
          <circle
            className="retreat-glow"
            cx={lantern.x}
            cy={lantern.y}
            r="14"
            fill={`url(#${id("lantern")})`}
            opacity="0.6"
          />
          <ellipse cx={lantern.x} cy={lantern.y} rx="5" ry="6" fill="#ffcf7a" />
          <path
            d={`M${lantern.x - 5} ${lantern.y} H${lantern.x + 5}`}
            stroke="#e08a2e"
            strokeWidth="0.6"
          />
          <rect
            x={lantern.x - 2.5}
            y={lantern.y - 7}
            width="5"
            height="1.6"
            fill="#5a3a22"
          />
        </g>
      ))}
      {PAMPAS.map(([x, y, r]) => (
        <g key={`pampas-${x}`} transform={`rotate(${r} ${x} 220)`}>
          <line
            x1={x}
            x2={x}
            y1={y + 10}
            y2="220"
            stroke="#a88a5a"
            strokeWidth="1"
          />
          <ellipse
            cx={x}
            cy={y}
            rx="4.5"
            ry="13"
            fill="#f4dfb8"
            opacity="0.92"
          />
          <ellipse
            cx={x - 1}
            cy={y - 2}
            rx="2"
            ry="9"
            fill="#fff4dc"
            opacity="0.8"
          />
        </g>
      ))}
    </svg>
  );
}

function RightBank({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute top-0 right-0"
      width="100"
      height="220"
      viewBox="200 0 100 220"
      aria-hidden="true"
      focusable="false"
    >
      {/* Right bank: bell tent with string lights */}
      <path
        d="M300 136 Q262 134 226 146 Q212 152 214 220 L300 220 Z"
        fill={`url(#${id("bank")})`}
      />
      <ellipse cx="256" cy="150" rx="40" ry="5" fill="#1a1d14" opacity="0.5" />
      <path
        d="M216 150 Q220 128 244 116 L256 98 L268 116 Q292 128 296 150 Z"
        fill={`url(#${id("tent")})`}
      />
      <path
        d="M256 98 L256 150"
        stroke="#b39373"
        strokeOpacity="0.5"
        strokeWidth="0.8"
      />
      <path d="M248 150 L256 118 L264 150 Z" fill="#ffb05a" />
      <path d="M251 150 L256 128 L261 150 Z" fill="#fff0c8" opacity="0.9" />
      <ellipse
        className="retreat-glow"
        cx="256"
        cy="150"
        rx="22"
        ry="6"
        fill="#ffc47a"
        opacity="0.45"
      />
      <path d="M256 98 L256 90" stroke="#6a4a2e" strokeWidth="1" />
      <path d="M256 90 L264 92 L256 94 Z" fill="#e07a5a" />
      <path
        d="M256 100 Q272 112 300 104"
        stroke="#3a2a22"
        strokeWidth="0.6"
        fill="none"
      />
      {[
        [264, 105.5],
        [272, 108],
        [281, 108],
        [290, 106.4],
      ].map(([x, y]) => (
        <circle key={`bulb-${x}`} cx={x} cy={y} r="1.6" fill="#fff0b8" />
      ))}
    </svg>
  );
}

export function DayRetreatsDisplayPreview({
  participants,
}: DayRetreatsDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="Day Retreats"
      backgroundClassName="bg-orange-300"
      participants={participants}
      scene={
        <>
          <style href="day-retreats-scene-keyframes" precedence="default">
            {RETREAT_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          {/* Sky + low sun (CSS keeps the disc round at any width) */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at 50% 44%, #fff8e4 0 13px, rgba(255,236,190,0.8) 15px, rgba(255,214,150,0.35) 34px, transparent 80px), radial-gradient(70% 60% at 50% 46%, rgba(255,214,150,0.55) 0%, transparent 70%), linear-gradient(180deg, #e58a7a 0%, #f6aa82 26%, #ffcf9a 44%, #ffe2b0 51%)",
            }}
          />
          <Landscape id={id} />
          <Shore id={id} />
          <LeftBank id={id} />
          <RightBank id={id} />
        </>
      }
      overlays={
        <div
          className="pointer-events-none absolute inset-0 z-10"
          style={{
            background:
              "radial-gradient(ellipse 120% 120% at 50% 45%, transparent 55%, rgba(40,20,30,0.45) 100%)",
          }}
        />
      }
    />
  );
}
